import { Alert, Col, Row } from "antd";
import { useContext, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import AuthContext from "../../../context/auth/context";
import AppContext from "../../../context/app/context";
import { IShoppingDaypart } from "../../../models/general-setting/response";
import { getProductStatus } from "../../../models/product/enum";
import { getReportType, ReportType } from "../../../models/report/enum";
import EndPoints from "../../../services/end-points";
import { Permissions } from "../../../utils/Permissions/Permissions";
import {
  getShoppingDaypartLabel,
  getShoppingDaypartsValue,
} from "../../../utils/helpers/shopping-dayparts";
import FieldBuilder from "../../form-components/field-builder";

const DATE_FORMAT = "YYYY-MM-DD";

const Form = () => {
  const { t, i18n } = useTranslation();

  const [deliveryWorkers, setDeliveryWorkers] = useState<any[]>();
  const [deliveryWorkersLoading, setDeliveryWorkersLoading] = useState(true);

  const [stores, setStores] = useState<any[]>();
  const [storesLoading, setStoresLoading] = useState(true);

  const [trademarks, setTrademarks] = useState<any[]>();
  const [trademarksLoading, setTrademarksLoading] = useState(true);

  const [discountCodes, setDiscountCodes] = useState<any[]>();
  const [discountCodesLoading, setDiscountCodesLoading] = useState(true);

  const [categories, setCategories] = useState<any[]>();
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [shoppingDayparts, setShoppingDayparts] = useState<IShoppingDaypart[]>(
    [],
  );
  const [shoppingDaypartsLoading, setShoppingDaypartsLoading] = useState(false);

  const { direction } = useContext(AppContext);
  const { userPermissions: permissions } = useContext(AuthContext);

  let userPermissions =
    permissions?.length > 0
      ? permissions
      : localStorage?.getItem("permisionStoroge")?.split(",");
  if (!userPermissions) userPermissions = [];

  const canExportProductMarginReport =
    userPermissions.includes(Permissions.CAN_EXPORT_PRODUCT_MARGIN_REPORT) &&
    userPermissions.includes(Permissions.CAN_MANAGE_PURCHASE_PRICE);
  const canExportSectorDeliveryTimeReport = userPermissions.includes(
    Permissions.CAN_EXPORT_SECTOR_DELIVERY_TIME_REPORT,
  );
  const canExportStoreAcceptanceTimeReport = userPermissions.includes(
    Permissions.CAN_EXPORT_STORE_ACCEPTANCE_TIME_REPORT,
  );
  const canExportPreparingTimeReport = userPermissions.includes(
    Permissions.CAN_EXPORT_PREPARING_TIME_REPORT,
  );
  const canExportOrderCycleTimeReport = userPermissions.includes(
    Permissions.CAN_EXPORT_ORDER_CYCLE_TIME_REPORT,
  );
  const canExportModifiedOrderAcceptanceTimeReport = userPermissions.includes(
    Permissions.CAN_EXPORT_MODIFIED_ORDER_ACCEPTANCE_TIME_REPORT,
  );
  const canExportAccountingFinalizationTimeReport = userPermissions.includes(
    Permissions.CAN_EXPORT_ACCOUNTING_FINALIZATION_TIME_REPORT,
  );
  const canExportDriverSearchTimeReport = userPermissions.includes(
    Permissions.CAN_EXPORT_DRIVER_SEARCH_TIME_REPORT,
  );
  const canExportCustomerCancellationReport = userPermissions.includes(
    Permissions.CAN_EXPORT_CUSTOMER_CANCELLATION_REPORT,
  );
  const canExportAdministrativeCancellationReport = userPermissions.includes(
    Permissions.CAN_EXPORT_ADMINISTRATIVE_CANCELLATION_REPORT,
  );
  const canExportCustomerSatisfactionReport = userPermissions.includes(
    Permissions.CAN_EXPORT_CUSTOMER_SATISFACTION_REPORT,
  );
  const canExportCouponImpactReport = userPermissions.includes(
    Permissions.CAN_EXPORT_COUPON_IMPACT_REPORT,
  );
  const canExportCartBehaviorReport =
    userPermissions.includes(Permissions.CAN_CART_BEHAVIOR_REPORT) ||
    userPermissions.includes(Permissions.CAN_EXPORT_CART_BEHAVIOR_REPORT);
  const canExportCategorySalesTimingReport = userPermissions.includes(
    Permissions.CAN_EXPORT_CATEGORY_SALES_TIMING_REPORT,
  );

  const type = [
    ReportType.PHARMACIES_ORDERS,
    ReportType.ORDER_USER_DETAILS,
    ReportType.ORDERS_CANCELLATION,
    ReportType.DRIVERS_PERFORMANCE,
    ReportType.ORDERS_DAYLE_INFO,
    ReportType.ORDERS_STATUS,
    ReportType.PAID_ORDERS_JOURNEY,
    ReportType.ORDERS_DETAILS,
    ReportType.DRIVER_PAYMENTS,
    ReportType.USER_ACTIVITY_REPORT,
    ReportType.MONTHLY_USER_ACTIVITY_REPORT,
    ReportType.LOW_STOCK_REPORT,
    ReportType.PRODUCT_BATCH_STOCK_REPORT,
    ...(canExportProductMarginReport ? [ReportType.PRODUCT_MARGIN_REPORT] : []),
    ...(canExportSectorDeliveryTimeReport
      ? [ReportType.SECTOR_DELIVERY_TIME_REPORT]
      : []),
    ...(canExportStoreAcceptanceTimeReport
      ? [ReportType.STORE_ACCEPTANCE_TIME_REPORT]
      : []),
    ...(canExportPreparingTimeReport ? [ReportType.PREPARING_TIME_REPORT] : []),
    ...(canExportOrderCycleTimeReport
      ? [ReportType.ORDER_CYCLE_TIME_REPORT]
      : []),
    ...(canExportModifiedOrderAcceptanceTimeReport
      ? [ReportType.MODIFIED_ORDER_ACCEPTANCE_TIME_REPORT]
      : []),
    ...(canExportAccountingFinalizationTimeReport
      ? [ReportType.ACCOUNTING_FINALIZATION_TIME_REPORT]
      : []),
    ...(canExportDriverSearchTimeReport
      ? [ReportType.DRIVER_SEARCH_TIME_REPORT]
      : []),
    ...(canExportCustomerCancellationReport
      ? [ReportType.CUSTOMER_CANCELLATION_REPORT]
      : []),
    ...(canExportAdministrativeCancellationReport
      ? [ReportType.ADMINISTRATIVE_CANCELLATION_REPORT]
      : []),
    ...(canExportCustomerSatisfactionReport
      ? [ReportType.CUSTOMER_SATISFACTION_REPORT]
      : []),
    ...(canExportCouponImpactReport ? [ReportType.COUPON_IMPACT] : []),
    ...(canExportCartBehaviorReport ? [ReportType.CART_BEHAVIOR_REPORT] : []),
    ...(canExportCategorySalesTimingReport
      ? [ReportType.CATEGORY_SALES_TIMING_REPORT]
      : []),
  ];

  const { control, setValue, getValues } = useFormContext();

  const typeWatch = useWatch({
    control,
    name: "type",
  });

  const startDateWatch = useWatch({
    control,
    name: "startDate",
  });

  const isLowStock = typeWatch === ReportType.LOW_STOCK_REPORT;
  const isProductMargin = typeWatch === ReportType.PRODUCT_MARGIN_REPORT;
  const isProductBatchStock =
    typeWatch === ReportType.PRODUCT_BATCH_STOCK_REPORT;
  const isCouponImpact = typeWatch === ReportType.COUPON_IMPACT;
  const isShoppingCartBehavior = typeWatch === ReportType.CART_BEHAVIOR_REPORT;
  const isCategorySalesTiming =
    typeWatch === ReportType.CATEGORY_SALES_TIMING_REPORT;
  const needsCategoryAndDaypartOptions =
    isShoppingCartBehavior || isCategorySalesTiming;

  const batchStockFilterModeWatch = useWatch({
    control,
    name: "batchStockFilterMode",
  });

  const couponSelectModeWatch = useWatch({
    control,
    name: "couponSelectMode",
  });

  const cartValueMinWatch = useWatch({
    control,
    name: "cartValueMin",
  });

  const minOrdersPerUserWatch = useWatch({
    control,
    name: "minOrdersPerUser",
  });

  const completionMinutesMinWatch = useWatch({
    control,
    name: "completionMinutesMin",
  });

  const today = dayjs().format(DATE_FORMAT);
  const isAfterToday = (value?: string) => Boolean(value && value > today);
  const validateNotFutureDate = (value?: string) =>
    !isAfterToday(value) || t("invalid_date");
  const validateReportEndDate = (value?: string) => {
    if (isAfterToday(value)) return t("invalid_date");
    if (startDateWatch && value && value < startDateWatch) {
      return t("end_date_before_start_date");
    }

    return true;
  };
  const hasFormValue = (value?: any) =>
    value !== undefined && value !== null && value !== "";
  const validateNonNegativeNumber = (value?: any) =>
    !hasFormValue(value) ||
    Number(value) >= 0 ||
    t("value_must_be_zero_or_more");
  const validateMaxNumber = (minValue?: any) => (value?: any) => {
    if (!hasFormValue(value) || !hasFormValue(minValue)) return true;

    return (
      Number(value) >= Number(minValue) || t("max_must_be_greater_than_min")
    );
  };
  const validateCartBehaviorEndDate = (value?: string) => {
    const endDateValidation = validateReportEndDate(value);
    if (endDateValidation !== true) return endDateValidation;

    if (startDateWatch && value) {
      const selectedDays = dayjs(value).diff(dayjs(startDateWatch), "day") + 1;
      if (selectedDays > 31) return t("cart_behavior_max_period_error");
    }

    return true;
  };
  const validateCategorySalesTimingEndDate = (value?: string) => {
    const endDateValidation = validateReportEndDate(value);
    if (endDateValidation !== true) return endDateValidation;

    if (startDateWatch && value) {
      const selectedDays = dayjs(value).diff(dayjs(startDateWatch), "day") + 1;
      if (selectedDays > 31) {
        return t("category_sales_timing_max_period_error");
      }
    }

    return true;
  };

  useEffect(() => {
    if (isProductBatchStock && !getValues("batchStockFilterMode")) {
      setValue("batchStockFilterMode", "expiration");
    }
  }, [isProductBatchStock, getValues, setValue]);

  useEffect(() => {
    if (isCouponImpact && !getValues("couponSelectMode")) {
      setValue("couponSelectMode", "select");
    }
  }, [isCouponImpact, getValues, setValue]);

  useEffect(() => {
    const getDeliveryWorker = async () => {
      try {
        const { data } = await EndPoints.deliveryWorkers.getAllDeliveryWorkers({
          light: true,
          perPage: -1,
        });
        setDeliveryWorkers(data?.data);
      } catch (error) {
      } finally {
        setDeliveryWorkersLoading(false);
      }
    };
    getDeliveryWorker();
  }, [direction]);

  useEffect(() => {
    const getStores = async () => {
      try {
        const { data } = await EndPoints.store.getAllStores({
          light: true,
          perPage: -1,
          filters: [{ name: "isNamlieah", value: 1, operation: "eq" }],
        });
        setStores(data?.data);
      } catch (error) {
      } finally {
        setStoresLoading(false);
      }
    };
    getStores();
  }, [direction]);

  useEffect(() => {
    const getTrademarks = async () => {
      try {
        const { data } = await EndPoints.trademark.getAllTrademark({
          light: true,
          perPage: -1,
        });
        setTrademarks(data?.data);
      } catch (error) {
      } finally {
        setTrademarksLoading(false);
      }
    };
    getTrademarks();
  }, [direction]);

  useEffect(() => {
    if (!canExportCouponImpactReport) {
      setDiscountCodesLoading(false);
      return;
    }
    const getDiscountCodes = async () => {
      try {
        const { data } = await EndPoints.discountCode.getAllDiscountCode({
          light: true,
          perPage: -1,
        });
        setDiscountCodes(data?.data);
      } catch (error) {
      } finally {
        setDiscountCodesLoading(false);
      }
    };
    getDiscountCodes();
  }, [direction, canExportCouponImpactReport]);

  useEffect(() => {
    if (!needsCategoryAndDaypartOptions) return;

    let isActive = true;

    const getCategoryAndDaypartOptions = async () => {
      setCategoriesLoading(true);
      setShoppingDaypartsLoading(true);

      try {
        const [categoriesResponse, settingsResponse] = await Promise.all([
          EndPoints.category.getAllCategories({
            light: true,
            perPage: -1,
          }),
          EndPoints.generalSettings.getGeneralSetting(),
        ]);

        if (!isActive) return;

        setCategories(categoriesResponse.data?.data ?? []);
        setShoppingDayparts(
          getShoppingDaypartsValue(settingsResponse.data?.shoppingDayparts),
        );
      } catch (error) {
        if (!isActive) return;

        setCategories([]);
        setShoppingDayparts(getShoppingDaypartsValue());
      } finally {
        if (isActive) {
          setCategoriesLoading(false);
          setShoppingDaypartsLoading(false);
        }
      }
    };

    getCategoryAndDaypartOptions();

    return () => {
      isActive = false;
    };
  }, [needsCategoryAndDaypartOptions, direction]);

  return (
    <Row gutter={[16, 8]}>
      <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
        <FieldBuilder
          name="type"
          label={`${t("type")}`}
          width="large"
          rules={{ required: true }}
          input={{
            type: "select",
            options: type.map((el) => {
              return {
                label: t(getReportType(el) as string),
                value: el,
              };
            }),
          }}
        />
      </Col>

      {isCategorySalesTiming ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="startDate"
              label={`${t("start_date")}`}
              rules={{ required: true, validate: validateNotFutureDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="endDate"
              label={`${t("end_date")}`}
              rules={{
                required: true,
                validate: validateCategorySalesTimingEndDate,
              }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
                disabled: !startDateWatch,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="storeIds"
              label={`${t("stores")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: storesLoading,
                allowSearch: true,
                options: stores?.map((store, index) => {
                  return {
                    key: index,
                    label: store?.name,
                    value: store?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="categoryIds"
              label={`${t("categories")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: categoriesLoading,
                allowSearch: true,
                options: categories?.map((category) => ({
                  label: category?.name,
                  value: category?.id,
                })),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="daypartKeys"
              label={`${t("shopping_dayparts")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: shoppingDaypartsLoading,
                allowSearch: true,
                options: shoppingDayparts.map((daypart) => ({
                  label: `${getShoppingDaypartLabel(daypart, i18n.language)} (${daypart.from} - ${daypart.to})`,
                  value: daypart.key,
                })),
              }}
            />
          </Col>
        </>
      ) : isShoppingCartBehavior ? (
        <>
          <Col span={24}>
            <Alert
              showIcon
              type="info"
              message={t("cart_behavior_report_note")}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="startDate"
              label={`${t("start_date")}`}
              rules={{ required: true, validate: validateNotFutureDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="endDate"
              label={`${t("end_date")}`}
              rules={{
                required: true,
                validate: validateCartBehaviorEndDate,
              }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
                disabled: !startDateWatch,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="cartValueMin"
              label={`${t("cart_value_min")}`}
              width="large"
              rules={{ validate: validateNonNegativeNumber }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="cartValueMax"
              label={`${t("cart_value_max")}`}
              width="large"
              rules={{
                validate: {
                  nonNegative: validateNonNegativeNumber,
                  range: validateMaxNumber(cartValueMinWatch),
                },
              }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="minOrdersPerUser"
              label={`${t("min_orders_per_user")}`}
              width="large"
              rules={{ validate: validateNonNegativeNumber }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="maxOrdersPerUser"
              label={`${t("max_orders_per_user")}`}
              width="large"
              rules={{
                validate: {
                  nonNegative: validateNonNegativeNumber,
                  range: validateMaxNumber(minOrdersPerUserWatch),
                },
              }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="deliveryTypes"
              label={`${t("delivery_type")}`}
              width="large"
              input={{
                type: "multiSelect",
                options: [
                  { label: t("express_delivery"), value: "express" },
                  { label: t("normal_delivery"), value: "normal" },
                ],
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="categoryIds"
              label={`${t("categories")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: categoriesLoading,
                allowSearch: true,
                options: categories?.map((category) => ({
                  label: category?.name,
                  value: category?.id,
                })),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="completionMinutesMin"
              label={`${t("completion_minutes_min")}`}
              width="large"
              rules={{ validate: validateNonNegativeNumber }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="completionMinutesMax"
              label={`${t("completion_minutes_max")}`}
              width="large"
              rules={{
                validate: {
                  nonNegative: validateNonNegativeNumber,
                  range: validateMaxNumber(completionMinutesMinWatch),
                },
              }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="daypartKeys"
              label={`${t("shopping_dayparts")}`}
              width="large"
              input={{
                type: "multiSelect",
                loading: shoppingDaypartsLoading,
                allowSearch: true,
                options: shoppingDayparts.map((daypart) => ({
                  label: `${getShoppingDaypartLabel(daypart, i18n.language)} (${daypart.from} - ${daypart.to})`,
                  value: daypart.key,
                })),
              }}
            />
          </Col>
        </>
      ) : isCouponImpact ? (
        <>
          <Col span={24}>
            <FieldBuilder
              name="couponSelectMode"
              label={t("coupon_select_mode")}
              width="large"
              rules={{ required: true }}
              input={{
                type: "radio",
                options: [
                  {
                    label: t("coupon_select_from_list"),
                    value: "select",
                  },
                  {
                    label: t("coupon_enter_manually"),
                    value: "manual",
                  },
                ],
              }}
            />
          </Col>
          {couponSelectModeWatch === "manual" ? (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
              <FieldBuilder
                name="couponCode"
                label={`${t("code")}`}
                width="large"
                rules={{ required: true }}
                input={{ type: "text" }}
              />
            </Col>
          ) : (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
              <FieldBuilder
                name="discountCodeId"
                label={`${t("discount_code")}`}
                width="large"
                rules={{ required: true }}
                input={{
                  type: "select",
                  loading: discountCodesLoading,
                  allowSearch: true,
                  options: discountCodes?.map((discountCode, index) => {
                    return {
                      key: index,
                      label: discountCode?.code
                        ? `${discountCode.code}${
                            discountCode?.name ? ` - ${discountCode.name}` : ""
                          }`
                        : discountCode?.name,
                      value: discountCode?.id,
                    };
                  }),
                }}
              />
            </Col>
          )}
        </>
      ) : isLowStock ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="store_id"
              label={`${t("store")}`}
              width="large"
              rules={{ required: true }}
              input={{
                type: "select",
                loading: storesLoading,
                options: stores?.map((store, index) => {
                  return {
                    key: index,
                    label: store?.name,
                    value: store?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="threshold"
              label={`${t("threshold")}`}
              width="large"
              rules={{ required: true, min: 0 }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="startDate"
              label={`${t("start_date")}`}
              rules={{ validate: validateNotFutureDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="endDate"
              label={`${t("end_date")}`}
              rules={{ validate: validateReportEndDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
                disabled: !startDateWatch,
              }}
            />
          </Col>
        </>
      ) : isProductBatchStock ? (
        <>
          <Col span={24}>
            <FieldBuilder
              name="batchStockFilterMode"
              label={t("batch_stock_filter_heading")}
              width="large"
              rules={{ required: true }}
              input={{
                type: "radio",
                options: [
                  {
                    label: t("batch_stock_filter_expiration"),
                    value: "expiration",
                  },
                  {
                    label: t("batch_stock_filter_remaining"),
                    value: "remaining",
                  },
                ],
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="store_id"
              label={`${t("store")}`}
              width="large"
              input={{
                type: "select",
                loading: storesLoading,
                allowSearch: true,
                options: stores?.map((store, index) => {
                  return {
                    key: index,
                    label: store?.name,
                    value: store?.id,
                  };
                }),
              }}
            />
          </Col>
          {batchStockFilterModeWatch === "remaining" ? (
            <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
              <FieldBuilder
                name="remaining_days"
                label={`${t("remaining_days_until_expiry")}`}
                width="large"
                rules={{ required: true, min: 0 }}
                input={{ type: "number" }}
              />
            </Col>
          ) : (
            <>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                <FieldBuilder
                  name="startDate"
                  label={`${t("start_date")}`}
                  rules={{ validate: validateNotFutureDate }}
                  input={{
                    type: "date-picker",
                    format: DATE_FORMAT,
                    showTime: false,
                  }}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                <FieldBuilder
                  name="endDate"
                  label={`${t("end_date")}`}
                  rules={{ validate: validateReportEndDate }}
                  input={{
                    type: "date-picker",
                    format: DATE_FORMAT,
                    showTime: false,
                  }}
                />
              </Col>
            </>
          )}
        </>
      ) : isProductMargin ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="store_id"
              label={`${t("store")}`}
              width="large"
              input={{
                type: "select",
                loading: storesLoading,
                allowSearch: true,
                options: stores?.map((store, index) => {
                  return {
                    key: index,
                    label: store?.name,
                    value: store?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="trademark_id"
              label={`${t("trademark")}`}
              width="large"
              input={{
                type: "select",
                loading: trademarksLoading,
                allowSearch: true,
                options: trademarks?.map((trademark, index) => {
                  return {
                    key: index,
                    label: trademark?.name,
                    value: trademark?.id,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="product_status"
              label={`${t("status")}`}
              width="large"
              input={{
                type: "select",
                options: [1, 2].map((status) => {
                  return {
                    label: t(getProductStatus(status) as string),
                    value: status,
                  };
                }),
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="store_quantity_status"
              label={`${t("store_status")}`}
              width="large"
              input={{
                type: "select",
                options: [1, 2].map((status) => {
                  return {
                    label: t(getProductStatus(status) as string),
                    value: status,
                  };
                }),
              }}
            />
          </Col>
        </>
      ) : (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="startDate"
              label={`${t("start_date")}`}
              rules={{ required: true, validate: validateNotFutureDate }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="endDate"
              label={`${t("end_date")}`}
              rules={{
                required: true,
                validate: validateReportEndDate,
              }}
              input={{
                type: "date-picker",
                format: DATE_FORMAT,
                showTime: false,
              }}
            />
          </Col>
        </>
      )}

      {typeWatch === ReportType.DRIVER_PAYMENTS ? (
        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
          <FieldBuilder
            name="driverId"
            label={`${t("delivery_worker")}`}
            width="large"
            rules={{ required: true }}
            input={{
              type: "select",
              loading: deliveryWorkersLoading,
              options: deliveryWorkers?.map((user, index) => {
                return {
                  key: index,
                  label: user?.firstName + " " + user?.lastName,
                  value: user?.id,
                };
              }),
            }}
          />
        </Col>
      ) : typeWatch === ReportType.USER_ACTIVITY_REPORT ? (
        <>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="min_orders"
              label={`${t("min_orders")}`}
              width="large"
              rules={{ min: 0 }}
              input={{ type: "number" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
            <FieldBuilder
              name="max_orders"
              label={`${t("max_orders")}`}
              width="large"
              rules={{ min: 0 }}
              input={{ type: "number" }}
            />
          </Col>
        </>
      ) : typeWatch === ReportType.MONTHLY_USER_ACTIVITY_REPORT ? (
        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
          <FieldBuilder
            name="min_orders_per_month"
            label={`${t("min_orders_per_month")}`}
            width="large"
            rules={{ required: true, min: 1 }}
            input={{ type: "number" }}
          />
        </Col>
      ) : null}
    </Row>
  );
};

const ReportForm = () => {
  return <Form />;
};

export default ReportForm;
